/**
 * color-gallery-sync.js
 *
 * Retail-only: When the user selects a color variant, the product media gallery
 * automatically updates to show images for the selected color.
 *
 * Architecture:
 * - Targets the NEW retail product page layout only
 *   (product-information section → variant-picker + slideshow-component gallery)
 * - Guards against wholesale pages via window.CF.templateSuffix
 * - Guards against featured-product-information sections (those are handled by
 *   the section-level morph in variant-picker.js itself)
 * - Uses document-level event delegation — safe for deferred/morphed DOM
 * - Does not modify media-gallery.js, variant-picker.js, or any theme core files
 *
 * Two-phase strategy:
 *
 * Phase 1 — "change" event (fires immediately on swatch click):
 *   Used when hide_variants is OFF (gallery contains all colors' images).
 *   Navigates the slideshow to the first slide whose image alt text matches
 *   the selected color name (partial, case-insensitive match).
 *
 * Phase 2 — "variant:update" event (fires after fetch + morph complete):
 *   Used when hide_variants is ON (gallery only shows current color's images).
 *   After variant-picker.js fetches the updated section HTML, the response
 *   contains a freshly server-rendered media-gallery filtered to the new color.
 *   We replace the stale gallery in the live DOM with the new one.
 *   Gallery re-initializes automatically (slideshow connectedCallback runs on
 *   the new element), starting at slide 0 of the new color.
 *
 * The WeakSet pendingColorChange ensures that ONLY color-option changes trigger
 * a gallery replacement — size/style changes leave the gallery untouched.
 *
 * Matching rules (Phase 1):
 * - Comparison is case-insensitive and trims whitespace
 * - Partial match (alt text CONTAINS the color name)
 * - If no matching image is found, the gallery is left unchanged (no error)
 * - If the target slide is already active, no navigation occurs (no flicker)
 *
 * @example
 * Variant "Black"    → img alt="black front view"  → ✓ match → navigate
 * Variant "Ocean Blue" → img alt="Ocean Blue Side" → ✓ match → navigate
 * Variant "Red"      → no img with "red" in alt    → no-op   → safe
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────
   * Guard: retail-only
   * Wholesale pages have window.CF.templateSuffix === 'wholesale'
   * (set in layout/theme.liquid)
   * ───────────────────────────────────────────── */
  function isRetailPage() {
    try {
      if (window.CF && window.CF.templateSuffix === 'wholesale') return false;
    } catch (_) {}
    return true;
  }

  /* ─────────────────────────────────────────────
   * Normalize a string: lowercase + trim + collapse whitespace
   * ───────────────────────────────────────────── */
  function normalize(str) {
    if (!str) return '';
    return String(str).toLowerCase().trim().replace(/\s+/g, ' ');
  }

  /* ─────────────────────────────────────────────
   * Check whether an image alt text partially matches the color name.
   * ───────────────────────────────────────────── */
  function altContainsColor(alt, color) {
    if (!alt || !color) return false;
    return normalize(alt).includes(normalize(color));
  }

  /* ─────────────────────────────────────────────
   * Extract the raw option name from a fieldset's <legend>.
   *
   * The legend HTML in variant-main-picker.liquid looks like:
   *   <legend>
   *     Color
   *     <span class="variant-option__swatch-value">Black</span>
   *   </legend>
   *
   * We collect only the TEXT nodes to get the bare option name ("Color").
   * ───────────────────────────────────────────── */
  function getOptionName(fieldset) {
    var legend = fieldset.querySelector('legend');
    if (!legend) return '';
    var text = Array.from(legend.childNodes)
      .filter(function (node) { return node.nodeType === Node.TEXT_NODE; })
      .map(function (node) { return node.textContent.trim(); })
      .join('');
    return normalize(text);
  }

  /* ─────────────────────────────────────────────
   * Find the media-gallery element that belongs to the same
   * product context as the given variant-picker.
   *
   * DOM path (retail):
   *   .product-information  (section wrapper)
   *     .product-information__media  → media-gallery
   *     .product-details             → variant-picker
   * ───────────────────────────────────────────── */
  function findGallery(variantPicker) {
    var container = variantPicker.closest('.product-information');
    if (!container) container = variantPicker.closest('[data-section]');
    if (!container) return null;
    return container.querySelector('media-gallery');
  }

  /* ─────────────────────────────────────────────
   * Navigate the slideshow inside a media-gallery to the first
   * slide whose image alt text contains the given color name.
   * Used when hide_variants is OFF (gallery shows all colors' images).
   * ───────────────────────────────────────────── */
  function syncGalleryToColor(gallery, colorName) {
    if (!gallery || !colorName) return;

    var slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;

    var allSlides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
    if (!allSlides.length) return;

    // Only search among visible slides (matches slideshow.slides getter)
    var visibleSlides = allSlides.filter(function (slide) {
      return !slide.hasAttribute('hidden') || slide.hasAttribute('reveal');
    });
    if (!visibleSlides.length) return;

    var targetIndex = -1;
    for (var i = 0; i < visibleSlides.length; i++) {
      var img = visibleSlides[i].querySelector('img');
      if (!img) continue;
      var alt = img.getAttribute('alt') || '';
      if (altContainsColor(alt, colorName)) {
        targetIndex = i;
        break;
      }
    }

    if (targetIndex === -1) return;              // no match — leave gallery unchanged
    if (targetIndex === slideshow.current) return; // already on target — no flicker
    slideshow.select(targetIndex);
  }

  /* ─────────────────────────────────────────────
   * WeakSet to track which variant-pickers have a pending
   * COLOR-option change (vs. size/style).
   * This ensures gallery replacement only fires for color swaps.
   * ───────────────────────────────────────────── */
  var pendingColorChange = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

  /* ─────────────────────────────────────────────
   * Phase 1 — "change" event handler.
   * Runs synchronously on swatch click.
   * Handles hide_variants:OFF navigation; also records that a color
   * change is in-flight so Phase 2 knows to update the gallery.
   * ───────────────────────────────────────────── */
  function onVariantPickerChange(event) {
    var target = event.target;
    if (!target) return;

    // Only radio inputs (swatches / buttons)
    if (target.tagName !== 'INPUT' || target.getAttribute('type') !== 'radio') return;

    var picker = target.closest('variant-picker');
    if (!picker) return;

    // Skip product cards and quick-add overlays
    if (target.closest('product-card') || target.closest('quick-add-dialog')) return;

    var fieldset = target.closest('fieldset');
    if (!fieldset) return;

    // Only act on the Color / Colour option
    var optionName = getOptionName(fieldset);
    if (optionName !== 'color' && optionName !== 'colour') return;

    var colorValue = target.value;
    if (!colorValue) return;

    // Mark that a color change is pending for this picker
    // (Phase 2 reads this to decide whether to replace the gallery)
    if (pendingColorChange) pendingColorChange.add(picker);

    // Phase 1: try to navigate in the current gallery
    // (Works immediately when gallery already has all colors' images)
    var gallery = findGallery(picker);
    if (!gallery) return;

    var capturedGallery = gallery;
    var capturedColor = colorValue;
    requestAnimationFrame(function () {
      syncGalleryToColor(capturedGallery, capturedColor);
    });
  }

  /* ─────────────────────────────────────────────
   * Phase 2 — "variant:update" event handler.
   * Fires after variant-picker.js completes the fetch + morph.
   * event.detail.data.html is the full updated page Document from DOMParser,
   * which includes a freshly server-rendered media-gallery filtered to the
   * new color (when hide_variants is ON).
   *
   * We extract that gallery and swap it in for the stale one in the live DOM.
   * The browser re-runs connectedCallback on slideshow-component → fresh init.
   *
   * Skip if:
   * - No pending color change (e.g., only size changed)
   * - Inside featured-product-information (the section morph already replaced
   *   the entire section including the gallery — no need to touch it again)
   * ───────────────────────────────────────────── */
  function onVariantUpdate(event) {
    if (!event.detail || !event.detail.data || !event.detail.data.html) return;

    var picker = event.target && event.target.closest
      ? event.target.closest('variant-picker')
      : null;
    if (!picker) return;

    // Skip product cards and quick-add overlays
    if (picker.closest('product-card') || picker.closest('quick-add-dialog')) return;

    // Skip featured-product-information sections — their section-level morph
    // in variant-picker.js already replaces the entire section incl. gallery
    if (picker.closest('featured-product-information')) return;

    // Only act if a COLOR option change triggered this update
    if (pendingColorChange) {
      if (!pendingColorChange.has(picker)) return;
      pendingColorChange.delete(picker);
    }

    // Find the stale gallery in the live DOM
    var currentGallery = findGallery(picker);
    if (!currentGallery) return;

    // Find the fresh gallery in the response document
    var newHtml = event.detail.data.html;
    // Prefer the gallery scoped inside the product-information wrapper for safety
    var newGallery = newHtml.querySelector('.product-information media-gallery');
    if (!newGallery) newGallery = newHtml.querySelector('media-gallery');
    if (!newGallery) return;

    // Adopt the node from the foreign DOMParser document into the live document,
    // then swap it in place of the stale gallery.
    // connectedCallback fires automatically on slideshow-component → fresh init at slide 0.
    var adopted = document.adoptNode(newGallery);

    // ── Transition: fade-in the incoming gallery ──────────────────────────
    // If the user prefers reduced motion, swap instantly (no animation).
    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      currentGallery.replaceWith(adopted);
      return;
    }

    // Start the new gallery at opacity 0, insert it, then fade to 1.
    // Double-rAF pattern: the first frame registers the DOM insertion,
    // the second frame starts the transition (avoids "transition not firing"
    // bugs in Safari / Chrome when element is newly connected).
    adopted.style.opacity = '0';
    currentGallery.replaceWith(adopted);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        adopted.style.transition = 'opacity 0.35s ease';
        adopted.style.opacity = '1';

        // Remove inline styles once the transition finishes so the element
        // is clean and any subsequent CSS rules apply normally.
        adopted.addEventListener('transitionend', function () {
          adopted.style.transition = '';
          adopted.style.opacity = '';
        }, { once: true });
      });
    });
  }

  /* ─────────────────────────────────────────────
   * Attach delegated listeners at the document level.
   * Document-level delegation survives section morphs after variant fetch.
   * ───────────────────────────────────────────── */
  function init() {
    if (!isRetailPage()) return;

    // Phase 1: immediate navigation for hide_variants:OFF galleries
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest('variant-picker')) return;
      onVariantPickerChange(event);
    });

    // Phase 2: gallery replacement after fetch completes (hide_variants:ON)
    document.addEventListener('variant:update', function (event) {
      if (!event.target || !event.target.closest) return;
      if (!event.target.closest('variant-picker')) return;
      onVariantUpdate(event);
    });
  }

  /* ─────────────────────────────────────────────
   * Boot
   * ───────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
